const getValue = (obj, key) => obj?.[key] || 0;

// export const hasTransaction = (section) => {
//   if (!section) return false;

//   return (
//     (section.cash?.amount || 0) > 0 ||
//     (section.pc?.weight || 0) > 0 ||
//     (section.lab?.pureWeight || 0) > 0 ||
//     (section.bar?.totalWeight || 0) > 0 ||
//     (section.coin?.pureWeight || 0) > 0 ||
//     (section.ginni?.pureWeight || 0) > 0 ||
//     (section.silver?.totalWeight || 0) > 0 ||
//     (section.goldRateWork?.weight || 0) > 0 ||
//     (section.silverRateWork?.weight || 0) > 0
//   );
// };

export const hasTransaction = (section) => {
  if (!section) return false;

  return (
    section.cash?.amount > 0 ||
    !!section.pc?.length ||
    !!section.lab?.length ||
    !!section.bar?.length ||
    !!section.coin?.length ||
    !!section.ginni?.length ||
    !!section.silver?.length ||
    section.goldRateWork ||
    section.silverRateWork
  );
};

export const initTotals = () => ({
  cash: 0,

  pc: { weight: 0, charges: 0, paidAmount: 0 },
  lab: { weight: 0, charges: 0, totalWeight: 0, paidAmount: 0 },
  bar: { weight: 0, charges: 0, paidAmount: 0 },
  coin: { weight: 0, charges: 0, paidAmount: 0 },
  ginni: { weight: 0, charges: 0, totalWeight: 0, paidAmount: 0 },
  silver: { weight: 0, charges: 0, silverBar: 0, paidAmount: 0 },

  rwg: { weight: 0, rate: 0, amount: 0 },
  rws: { weight: 0, rate: 0, amount: 0 },
});

export const accumulateTotals = (total, row) => {
  total.cash += row.cash;

  ["pc", "lab", "bar", "coin", "ginni", "silver"].forEach((key) => {
    total[key].weight += row[key].weight || 0;
    total[key].charges += row[key].charges || 0;
    total[key].paidAmount += row[key].paidAmount || 0;
    total[key].totalWeight += row[key].totalWeight || 0;
  });

  // total.silver.weight += row.silver.weight || 0;
  // total.silver.charges += row.silver.charges || 0;
  total.silver.silverBar += row.silver.silverBar || 0;
  // total.silver.paidAmount += row.silver.paidAmount || 0;

  total.rwg.weight += row.rwg.weight || 0;
  total.rwg.rate += row.rwg.rate || 0;
  total.rwg.amount += row.rwg.amount || 0;

  total.rws.weight += row.rws.weight || 0;
  total.rws.rate += row.rws.rate || 0;
  total.rws.amount += row.rws.amount || 0;
};

export const subtractObjects = (obj1, obj2, parentKey = "") => {
  const result = {};

  Object.keys(obj1).forEach((key) => {
    if (
      typeof obj1[key] === "object" &&
      obj1[key] !== null &&
      !Array.isArray(obj1[key])
    ) {
      result[key] = subtractObjects(
        obj1[key],
        obj2[key] || {},
        key, // pass current parent key
      );
    } else {
      // For lab/ginni => add charges & paidAmount
      if (
        (parentKey === "lab" || parentKey === "ginni") &&
        (key === "charges" || key === "paidAmount")
      ) {
        result[key] = (obj1[key] || 0) + (obj2[key] || 0);
      } else {
        // Default subtraction
        result[key] = (obj1[key] || 0) - (obj2[key] || 0);
      }
    }
  });

  return result;
};

export const generateReport = (transactions) => {
  const receiveRows = [];
  const givenRows = [];

  const totals = {
    receive: initTotals(),
    given: initTotals(),
  };

  transactions.forEach((tx) => {
    const receive = tx.receive || {};
    const given = tx.given || {};

    const hasReceive = hasTransaction(receive);
    const hasGiven = hasTransaction(given);

    if (hasReceive) {
      const r = buildRow(tx, receive, tx.serialNo, "receive");
      receiveRows.push(r);
      accumulateTotals(totals.receive, r);
    }

    if (hasGiven) {
      const g = buildRow(tx, given, tx.serialNo, "given");
      givenRows.push(g);
      accumulateTotals(totals.given, g);
    }
  });

  const balance = {};

  Object.keys(totals.receive).forEach((key) => {
    balance[key] =
      key === "cash"
        ? totals.receive[key] - totals.given[key]
        : subtractObjects(totals.receive[key], totals.given[key]);
  });

  return {
    receive: { rows: receiveRows, totals: totals.receive },
    given: { rows: givenRows, totals: totals.given },
    balance,
  };
};

const buildRow = (tx, data, serial, side) => {
  return {
    txId: tx._id,
    serial,
    side,
    date: tx.transactionDate,
    party: tx.customerId?.name || "-",

    cash: data.cash?.amount || 0,

    pc: {
      weight: getTotal(data?.pc, "netWeight") || 0,
      charges: getTotal(data?.pc, "netAmount") || 0,
      paidAmount: getTotal(data?.pc, "paidAmount") || 0,
    },

    lab: {
      weight: getTotal(data?.lab, "netWeight") || 0,
      charges: getTotal(data?.lab, "netAmount") || 0,
      totalWeight: getTotal(data?.lab, "weight") || 0,
      paidAmount: getTotal(data?.lab, "paidAmount") || 0,
    },

    bar: {
      weight: getTotal(data?.bar, "netWeight") || 0,
      charges: getTotal(data?.bar, "netAmount") || 0,
      paidAmount: getTotal(data?.bar, "paidAmount") || 0,
    },

    coin: {
      weight: getTotal(data?.coin, "netWeight") || 0,
      charges: getTotal(data?.coin, "netAmount") || 0,
      paidAmount: getTotal(data?.coin, "paidAmount") || 0,
    },

    ginni: {
      weight: getTotal(data?.ginni, "netWeight") || 0,
      charges: getTotal(data?.ginni, "netAmount") || 0,
      totalWeight: getTotal(data?.ginni, "") || 0,
      paidAmount: getTotal(data?.ginni, "paidAmount") || 0,
    },

    silver: {
      weight: getTotal(data?.silver, "netWeight") || 0,
      charges: getTotal(data?.silver, "netAmount") || 0,
      silverBar: getTotal(data?.silver, "barWeight") || 0,
      paidAmount: getTotal(data?.silver, "paidAmount") || 0,
    },

    rwg: {
      weight: data?.goldRateWork?.netWeight || 0,
      amount: data?.goldRateWork?.netAmount || 0,
      rate: data?.goldRateWork?.rate || 0,
    },

    rws: {
      weight: data?.silverRateWork?.netWeight || 0,
      amount: data?.silverRateWork?.netAmount || 0,
      rate: data?.silverRateWork?.rate || 0,
    },
  };
};

export const getTransactionById = (originalData = [], id) => {
  return originalData?.find((tx) => tx._id === id);
};

function getTotal(section = [], field) {
  if (!Array.isArray(section) || !field) return 0;

  return section.reduce((total, item) => {
    return total + Number(item?.[field] || 0);
  }, 0);
}
